import {simplifyQuestions} from 'utils/user_bio';

const ENTITY_TYPES = {
  CAREGIVER: 'CAREGIVER',
  CLIENT: 'CLIENT',
};

const CHANNELS = {
  SMS: 'SMS',
  EMAIL: 'EMAIL',
  SUPPORT_CHAT: 'SUPPORT_CHAT',
};

const PROFILE_CATEGORY_SIGNAL_KEYS = {
  contact: [
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'streetAddress',
    'city',
    'province',
    'postalCode',
  ],
  profile: [
    'profilePhoto',
    'shortBio',
    'personalBio',
    'caregiverMotivation',
  ],
  experience: [
    'languages',
    'certificates',
    'workHistory',
    'references',
    'education',
  ],
  questionnaire: [
    'preferences',
    'availability',
    'skillsExperienceCompleted',
    'personalityCompleted',
  ],
  agreement: [
    'termsAgreement',
    'certifiesTruthfulness',
    'understandsPrivacyAndAgreesToConfidentialityPolicies',
    'certifiesValidAndUpToDateCertifications',
    'understandsAbuseProhibition',
    'committedToCodeOfConductAndEthics',
  ],
  administrative: [
    'canadianId',
    'canadianIdDocuments',
    'vulnerableSectorCheck',
    'vulnerableSectorCheckDocuments',
  ],
};

const PROFILE_CATEGORY_KEYS = Object.keys(PROFILE_CATEGORY_SIGNAL_KEYS);

const hasText = (value) => {
  return Boolean(String(value || '').trim());
};

const getCaregiverProfileSignals = (item) => {
  const questions = simplifyQuestions(item.bio?.questions ?? {});
  const workHistory = Array.isArray(questions.workHistory) ? questions.workHistory : [];
  const references = Array.isArray(questions.references) ? questions.references : [];
  const educationEntries = Array.isArray(questions.education?.entries) ? questions.education.entries : [];

  return {
    firstName: hasText(questions.firstName),
    lastName: hasText(questions.lastName),
    email: hasText(questions.email),
    phoneNumber: hasText(questions.phoneNumber),
    streetAddress: hasText(questions.streetAddress),
    city: hasText(questions.city),
    province: hasText(questions.province),
    postalCode: hasText(questions.postalCode),
    profilePhoto: Array.isArray(questions.profilePhoto) && questions.profilePhoto.length > 0,
    shortBio: hasText(questions.shortBio),
    personalBio: hasText(questions.personalBio),
    caregiverMotivation: hasText(questions.caregiverMotivation),
    languages: Array.isArray(questions.languages) && questions.languages.length > 0,
    certificates: Array.isArray(questions.certificates) && questions.certificates.length > 0,
    workHistory: workHistory.some((entry) => hasText(entry?.employerName) && entry?.startDate),
    references: references.some((entry) => hasText(entry?.name)),
    education: educationEntries.some((entry) => hasText(entry?.id)),
    preferences: Boolean(questions.preferences),
    availability: Boolean(questions.availability),
    skillsExperienceCompleted: questions.skillsExperienceCompleted === true,
    personalityCompleted: questions.personalityCompleted === true,
    termsAgreement: questions.termsAgreement === true,
    certifiesTruthfulness: questions.certifiesTruthfulness === true,
    understandsPrivacyAndAgreesToConfidentialityPolicies:
      questions.understandsPrivacyAndAgreesToConfidentialityPolicies === true,
    certifiesValidAndUpToDateCertifications: questions.certifiesValidAndUpToDateCertifications === true,
    understandsAbuseProhibition: questions.understandsAbuseProhibition === true,
    committedToCodeOfConductAndEthics: questions.committedToCodeOfConductAndEthics === true,
    canadianId: questions.canadianId?.hasId === true,
    canadianIdDocuments: Array.isArray(questions.canadianId?.documents) &&
      questions.canadianId.documents.length > 0,
    vulnerableSectorCheck: questions.vulnerableSectorCheck?.hasCheck === true,
    vulnerableSectorCheckDocuments: Array.isArray(questions.vulnerableSectorCheck?.documents) &&
      questions.vulnerableSectorCheck.documents.length > 0,
  };
};

const getCaregiverProfileCategories = (item) => {
  const signals = getCaregiverProfileSignals(item);

  return Object.fromEntries(Object.entries(PROFILE_CATEGORY_SIGNAL_KEYS).map(([category, signalKeys]) => {
    return [category, signalKeys.every((signal) => signals[signal])];
  }));
};

const matchesProfileCategoryFilters = (item, missingCategories) => {
  const categories = getCaregiverProfileCategories(item);
  const matchesMissingCategory = missingCategories.some((category) => !categories[category]);

  return matchesMissingCategory;
};

const getResourceRn = (item, entityType) => {
  return entityType === ENTITY_TYPES.CAREGIVER ? item.caregiverRn : item.clientRn;
};

const getEntityName = (item, entityType) => {
  if (entityType === ENTITY_TYPES.CLIENT) {
    return item.clientName || item.clientRn;
  }

  const bio = simplifyQuestions(item.bio?.questions ?? {});
  const bioName = [bio.firstName, bio.lastName].filter(Boolean).join(' ').trim();
  return item.caregiverName || bioName || item.caregiverRn;
};

const getPrimaryRelationship = (item) => {
  const relationships = Object.values(item.relationships ?? {}).filter((relationship) => {
    return relationship?.relationshipRn;
  });

  return relationships.sort((firstRelationship, secondRelationship) => {
    const firstPriority = Number(firstRelationship.priority);
    const secondPriority = Number(secondRelationship.priority);
    const normalizedFirstPriority = Number.isFinite(firstPriority) ? firstPriority : Number.POSITIVE_INFINITY;
    const normalizedSecondPriority = Number.isFinite(secondPriority) ? secondPriority : Number.POSITIVE_INFINITY;

    if (normalizedFirstPriority !== normalizedSecondPriority) {
      return normalizedFirstPriority - normalizedSecondPriority;
    }

    return (firstRelationship.name || '').localeCompare(secondRelationship.name || '');
  })[0] || null;
};

const normalizeContactType = (contactType) => {
  return String(contactType || '').replace(/[^a-z]/gi, '').toUpperCase();
};

const getRelationshipContact = (relationship, channel) => {
  const contactTypes = channel === CHANNELS.EMAIL ? ['EMAIL'] : ['PHONE', 'PHONENUMBER', 'SMS'];
  const contact = (relationship?.contacts || []).find((relationshipContact) => {
    return contactTypes.includes(normalizeContactType(relationshipContact?.type));
  });

  return contact?.detail || '';
};

const getCaregiverContact = (item, channel) => {
  const bio = simplifyQuestions(item.bio?.questions ?? {});
  return channel === CHANNELS.EMAIL ? String(bio.email || '').trim() : String(bio.phoneNumber || '').trim();
};

const createRecipient = (item, entityType, channel, supportConversations) => {
  const resourceRn = getResourceRn(item, entityType);
  const entityName = getEntityName(item, entityType);
  const baseRecipient = {
    id: resourceRn,
    item,
    resourceRn,
    entityName,
    status: item.status,
    targetRn: null,
    recipientName: '',
    contact: '',
    conversationRn: null,
    eligible: false,
    eligibilityPending: false,
    skipReason: null,
  };

  if (!channel) {
    return baseRecipient;
  }

  if (entityType === ENTITY_TYPES.CLIENT) {
    const primaryRelationship = getPrimaryRelationship(item);
    if (!primaryRelationship) {
      return {
        ...baseRecipient,
        skipReason: 'MissingPrimaryRelationship',
      };
    }

    const contact = getRelationshipContact(primaryRelationship, channel);
    return {
      ...baseRecipient,
      targetRn: primaryRelationship.relationshipRn,
      recipientName: primaryRelationship.name || primaryRelationship.relationshipRn,
      contact,
      eligible: Boolean(contact),
      skipReason: contact ? null : channel === CHANNELS.EMAIL ? 'MissingEmail' : 'MissingPhone',
    };
  }

  if (channel === CHANNELS.SUPPORT_CHAT) {
    const eligibilityPending = !Object.prototype.hasOwnProperty.call(supportConversations, resourceRn);
    const conversationRn = supportConversations[resourceRn] || null;
    return {
      ...baseRecipient,
      targetRn: resourceRn,
      recipientName: entityName,
      contact: conversationRn || '',
      conversationRn,
      eligible: Boolean(conversationRn),
      eligibilityPending,
      skipReason: !eligibilityPending && !conversationRn ? 'MissingSupportConversation' : null,
    };
  }

  const contact = getCaregiverContact(item, channel);
  return {
    ...baseRecipient,
    targetRn: resourceRn,
    recipientName: entityName,
    contact,
    eligible: Boolean(contact),
    skipReason: contact ? null : channel === CHANNELS.EMAIL ? 'MissingCaregiverEmail' : 'MissingCaregiverPhone',
  };
};

export {
  CHANNELS,
  ENTITY_TYPES,
  PROFILE_CATEGORY_SIGNAL_KEYS,
  PROFILE_CATEGORY_KEYS,
  createRecipient,
  getCaregiverProfileCategories,
  matchesProfileCategoryFilters,
};
